import omit from 'lodash/omit';
import { matchSorter } from 'match-sorter';
import { forwardRef, memo, useState, Ref } from 'react';
import { Autocomplete, TextField, AutocompleteValue, MenuItem, useTheme, styled } from '@mui/material';
import { IArgumentMetadata } from '@tymely/atoms';
import usePolicyParams from '@tymely/pages/GraphPolicyEditor/hooks/usePolicyParams';
import { useSetAlert } from '@tymely/services';

import { styledPopper } from '../Popper';
import AutocompleteVirtualizedListbox from '../AutocompleteVirtualizedListbox';

export const DEFAULT_ARG_METADATA: IArgumentMetadata = {
    id: -1,
    name: '',
    title: '',
    description: '',
    extractor_name: 'no_value',
    dtype: 'str',
    arg_type: 'TEXT_ARGUMENT',
    scope: 'COMMENT',
    is_list: false,
    is_ticket_arg: false,
    unspecifiable: true,
    unknowable: false,
    dependencies: [],
    params: {},
    additional_data: {},
    created_date: new Date(),
};

const StyledTextField = styled(TextField)`
    .MuiInputBase-root {
        background-color: white;
    }
`;

export const ArgSearchBox = memo(
    forwardRef(
        (
            props: {
                hint: string;
                args: IArgumentMetadata[];
                getOptionDisabled: (arg: IArgumentMetadata) => boolean;
                addNewArgument: (arg: Omit<IArgumentMetadata, 'id'>) => void;
                onSelect?: (arg: IArgumentMetadata) => void;
                onBlur?: () => void;
                disabled?: boolean;
            },
            textFieldRef: Ref<HTMLInputElement>,
        ) => {
            const { organizationId } = usePolicyParams();
            const showAlert = useSetAlert();
            const [value, setValue] = useState<IArgumentMetadata | null>(null);
            const [inputValue, setInputValue] = useState('');

            const handleSelectOption = (
                newValue: AutocompleteValue<IArgumentMetadata | string, undefined, undefined, undefined>,
            ) => {
                if (typeof newValue === 'string') {
                    props.addNewArgument({ ...omit(DEFAULT_ARG_METADATA, 'id'), name: newValue });
                } else if (newValue?.id === -1) {
                    props.addNewArgument({ ...omit(DEFAULT_ARG_METADATA, 'id'), name: newValue.name });
                } else {
                    if (newValue) {
                        if (
                            newValue.params.only_orgs &&
                            !(newValue.params.only_orgs as number[]).includes(organizationId)
                        ) {
                            showAlert(
                                `Argument ${newValue.name} does not include your organization in only_orgs`,
                                'error',
                            );
                        }
                        props.onSelect?.(newValue);
                    }
                    setValue(null);
                }
                setInputValue('');
            };

            const theme = useTheme();

            return (
                <Autocomplete
                    id="argument-selector"
                    disabled={props.disabled}
                    inputValue={inputValue}
                    ListboxComponent={AutocompleteVirtualizedListbox}
                    onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                    value={value}
                    onChange={(_, newValue) => handleSelectOption(newValue)}
                    onBlur={props.onBlur}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                    }}
                    PopperComponent={styledPopper({ width: 'fit-content', maxWidth: theme.spacing(60) })}
                    filterOptions={(options, params: { inputValue: string }) => {
                        const { inputValue } = params;
                        const filtered = matchSorter(options, inputValue, {
                            keys: ['name', 'title'],
                        });

                        if (
                            params.inputValue !== '' &&
                            props.args.filter((arg) => arg.title === params.inputValue).length === 0
                        ) {
                            filtered.push({ ...DEFAULT_ARG_METADATA, name: params.inputValue });
                        }

                        return filtered;
                    }}
                    options={props.args}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                    renderOption={(_, option) => {
                        if (
                            option.id === -1 &&
                            props.args.find((arg) => arg.name.toLowerCase() === option.name.toLowerCase())
                        ) {
                            return null;
                        }

                        return (
                            <MenuItem
                                key={option.id}
                                title={option.name}
                                onClick={() => handleSelectOption(option)}
                                disabled={props.getOptionDisabled(option)}
                            >
                                {option.id === -1 ? `Add "${option.name}"` : option.name}
                            </MenuItem>
                        );
                    }}
                    selectOnFocus
                    blurOnSelect
                    clearOnBlur
                    fullWidth
                    size="small"
                    freeSolo
                    renderInput={(params) => <StyledTextField {...params} inputRef={textFieldRef} label={props.hint} />}
                />
            );
        },
    ),
);
ArgSearchBox.displayName = 'ArgSearchBox';
